import { createSlice } from "@reduxjs/toolkit";

/**
 * God Mode — internal operator access to a client's account.
 *
 * The operator is never a staff member of the center they enter: the teacher
 * object comes from the API and is never written to the school's /teachers node,
 * so the client's staff list, seat count and Teams & Roles screen are unchanged.
 * Sessions start read-only; writing requires an explicit, reasoned elevation.
 */
export const godModeSlice = createSlice({
    name: "godMode",
    initialState: {
        /** Whether the signed-in account is on the operator allowlist. */
        isOperator: false,
        /** False until the status check has answered once, so the login screen
         *  can avoid flashing the picker. */
        statusChecked: false,
        sessionTtlMinutes: 60,
        operator: null,
        centers: [],
        centersLoading: false,
        centerSearch: "",
        /** The live session, mirrored from local storage for rendering. */
        session: null,
        isLoading: false,
        error: null,
        operationType: undefined,
    },
    reducers: {
        fetchGodModeStatus: (state, action) => {
            state.isLoading = true;
        },
        setGodModeStatus: (state, action) => {
            state.isOperator = action.payload.isOperator;
            state.operator = action.payload.operator || null;
            state.sessionTtlMinutes = action.payload.sessionTtlMinutes || 60;
            state.statusChecked = true;
            state.isLoading = false;
        },
        fetchGodModeCenters: (state, action) => {
            state.centersLoading = true;
            state.centerSearch = action.payload?.search || "";
        },
        setGodModeCenters: (state, action) => {
            state.centers = action.payload.centers || [];
            state.centersLoading = false;
        },
        startGodModeSession: (state, action) => {
            state.isLoading = true;
            state.error = null;
            state.operationType = undefined;
        },
        godModeSessionStarted: (state, action) => {
            state.session = action.payload.session;
            state.isLoading = false;
            state.operationType = "GOD_MODE_SESSION_STARTED";
        },
        elevateGodModeSession: (state, action) => {
            state.isLoading = true;
        },
        godModeSessionElevated: (state, action) => {
            state.session = action.payload.session;
            state.isLoading = false;
            state.operationType = "GOD_MODE_SESSION_ELEVATED";
        },
        /**
         * Leave the current client and go back to the picker. This does NOT sign
         * the operator out: the Auth0 and master Firebase sessions are theirs, not
         * the client's, so only the client-scoped session is torn down.
         */
        switchGodModeClient: (state, action) => {
            state.isLoading = true;
        },
        /** Poll trigger — handled entirely in the saga. */
        refreshGodModeSession: (state, action) => state,
        /** Teardown finished. The banner watches for this and routes to the picker. */
        godModeSwitchReady: (state, action) => {
            state.session = null;
            state.centers = [];
            state.centerSearch = "";
            state.isLoading = false;
            state.operationType = "GOD_MODE_SWITCH_READY";
        },
        setGodModeSessionState: (state, action) => {
            state.session = action.payload.session;
        },
        godModeFailed: (state, action) => {
            state.isLoading = false;
            state.centersLoading = false;
            state.error = action.payload?.error || "God Mode request failed";
            state.operationType = "GOD_MODE_FAILED";
        },
        resetGodModeOperationType: (state, action) => {
            state.operationType = undefined;
            state.error = null;
        },
    },
});

export const {
    fetchGodModeStatus,
    setGodModeStatus,
    fetchGodModeCenters,
    setGodModeCenters,
    startGodModeSession,
    godModeSessionStarted,
    elevateGodModeSession,
    godModeSessionElevated,
    switchGodModeClient,
    refreshGodModeSession,
    godModeSwitchReady,
    setGodModeSessionState,
    godModeFailed,
    resetGodModeOperationType,
} = godModeSlice.actions;

export default godModeSlice.reducer;
